<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import feather from "feather-icons";

const THEMES = [
  "light",
  "dark",
  "cupcake",
  "bumblebee",
  "emerald",
  "corporate",
  "synthwave",
  "retro",
  "cyberpunk",
  "valentine",
  "halloween",
  "garden",
  "forest",
  "aqua",
  "lofi",
  "pastel",
  "fantasy",
  "wireframe",
  "black",
  "luxury",
  "dracula",
  "cmyk",
  "autumn",
  "business",
  "acid",
  "lemonade",
  "night",
  "coffee",
  "winter",
  "dim",
  "nord",
  "sunset",
] as const;

const DARK_THEMES = new Set([
  "dark",
  "synthwave",
  "halloween",
  "forest",
  "black",
  "luxury",
  "dracula",
  "business",
  "night",
  "coffee",
  "dim",
  "sunset",
]);

const currentTheme = ref("light");
const search = ref("");

const isDark = computed(() => DARK_THEMES.has(currentTheme.value));

const filteredThemes = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return THEMES;
  return THEMES.filter((t) => t.toLowerCase().includes(q));
});

onMounted(() => {
  const saved = localStorage.getItem("theme") || "light";
  currentTheme.value = saved;
  document.documentElement.setAttribute("data-theme", saved);
});

function setTheme(theme: string) {
  currentTheme.value = theme;
  localStorage.setItem("theme", theme);
  document.documentElement.setAttribute("data-theme", theme);
  search.value = "";
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
}
</script>

<template>
  <div class="dropdown dropdown-end">
    <button
      tabindex="0"
      type="button"
      class="btn btn-ghost hover:bg-inherit"
      :title="`Theme: ${currentTheme}`"
      aria-label="Theme wechseln"
    >
      <i v-html="feather.icons[isDark ? 'moon' : 'sun'].toSvg()"></i>
    </button>
    <div
      tabindex="0"
      class="dropdown-content z-50 p-2 shadow-2xl bg-base-200 border border-base-content/10 rounded-box w-60 max-h-96 overflow-y-auto block font-sans"
    >
      <!-- Dropdown header & search -->
      <div class="px-2 py-1 mb-2 border-b border-base-content/10">
        <div class="flex items-center justify-between mb-1.5">
          <span class="text-xs font-bold uppercase tracking-wider opacity-70">Design</span>
          <span class="badge badge-xs badge-primary font-mono text-[10px]">{{ currentTheme }}</span>
        </div>
        <input
          v-model="search"
          type="text"
          placeholder="Filter…"
          class="input input-xs input-bordered w-full text-xs"
          @click.stop
        />
      </div>

      <!-- Theme list -->
      <ul class="menu menu-xs p-0 gap-0.5">
        <li v-for="t in filteredThemes" :key="t">
          <button
            type="button"
            class="flex items-center justify-between w-full px-2 py-1.5 rounded-lg text-xs"
            :class="{ 'active font-bold': currentTheme === t }"
            :data-theme="t"
            @click="setTheme(t)"
          >
            <span class="capitalize flex items-center gap-1.5">
              <span v-if="currentTheme === t" class="text-primary font-bold">✓</span>
              <span :class="{ 'pl-3.5': currentTheme !== t }">{{ t }}</span>
            </span>
            <!-- Live swatch preview in theme colors -->
            <span class="flex gap-1 items-center bg-base-100 px-1 py-0.5 rounded border border-base-content/15 shadow-sm">
              <span class="w-2 h-2 rounded-full bg-primary" />
              <span class="w-2 h-2 rounded-full bg-secondary" />
              <span class="w-2 h-2 rounded-full bg-accent" />
              <span class="w-2 h-2 rounded-full bg-neutral" />
            </span>
          </button>
        </li>
        <li v-if="!filteredThemes.length" class="p-2 text-center text-xs opacity-60">
          Kein Theme gefunden
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.dropdown-content {
  scrollbar-width: thin;
}
</style>
